import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { timingBridgeAPI, showsAPI, toolsAPI } from '../lib/api';
import { HealthPill } from '../components/tools/climbBridge/BridgeHealth';
import SetupPanel from '../components/tools/timingBridge/SetupPanel';
import LivePanel from '../components/tools/timingBridge/LivePanel';
import DiagnosticsPanel from '../components/tools/timingBridge/DiagnosticsPanel';
import RecordingsPanel from '../components/tools/timingBridge/RecordingsPanel';

const TOOL_KEY = 'timing-bridge';

const TABS = [
  { key: 'setup', label: 'Setup' },
  { key: 'live', label: 'Live' },
  { key: 'diagnostics', label: 'Diagnostics' },
  { key: 'recordings', label: 'Recordings' },
];

/** Tabs that make sense with no bridge running. */
const ALWAYS_OPEN = new Set(['setup', 'recordings']);

/**
 * Motorsport Live Timing.
 *
 * Follows a Speedhive, Alpha Timing or Apex Timing live timing page and pushes
 * broadcast-ready payloads to Singular data streams — including the legacy
 * scraper's document, so graphics built for the old terminal tool keep working.
 *
 * Three screens: point it at the timing page (and see the running order before
 * anything publishes), run the show with it, and find out why when a number
 * looks wrong.
 */
export default function TimingBridgePage() {
  const [tool, setTool] = useState(null);
  const [shows, setShows] = useState([]);
  const [showId, setShowId] = useState('');
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState('setup');
  const [error, setError] = useState('');
  const [stopping, setStopping] = useState(false);

  const running = !!summary?.running;
  const playingBack = running && summary?.mode === 'playback';
  const show = shows.find(s => s.id === showId) || null;

  useEffect(() => {
    toolsAPI.list()
      .then(res => setTool((res.data.tools || []).find(t => t.key === TOOL_KEY) || null))
      .catch(() => {});

    showsAPI.list()
      .then(res => setShows(Array.isArray(res.data) ? res.data : (res.data?.shows || [])))
      .catch(err => setError(err.response?.data?.error || 'Could not load shows'));
  }, []);

  // A bridge already running in this workspace wins the initial show choice —
  // an operator opening this mid-race wants the live one.
  useEffect(() => {
    if (showId || shows.length === 0) return;
    timingBridgeAPI.status()
      .then(res => {
        const live = (res.data.bridges || [])[0];
        setShowId(live?.showId || shows[0]?.id || '');
        if (live) setTab('live');
      })
      .catch(() => setShowId(shows[0]?.id || ''));
  }, [shows, showId]);

  const refresh = useCallback(() => {
    if (!showId) return Promise.resolve();
    return timingBridgeAPI.status(showId).then(res => setSummary(res.data)).catch(() => {});
  }, [showId]);

  // 1 s polling while the page is open: /status reads in-memory state only.
  useEffect(() => {
    if (!showId) return undefined;
    let cancelled = false;
    const load = () => timingBridgeAPI.status(showId)
      .then(res => { if (!cancelled) setSummary(res.data); })
      .catch(() => {});
    load();
    const timer = setInterval(load, 1000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [showId]);

  useEffect(() => {
    if (running && tab === 'setup') setTab('live');
    if (!running && !ALWAYS_OPEN.has(tab)) setTab('setup');
  }, [running]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleStop() {
    setStopping(true);
    setError('');
    try {
      await timingBridgeAPI.stop(showId);
      await refresh();
      setTab('setup');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not stop live timing');
    } finally {
      setStopping(false);
    }
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      <Link to="/tools" className="text-xs text-blue-500 hover:text-blue-400 transition-colors flex items-center gap-1 mb-4">
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Tools
      </Link>

      <div className="mb-5 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-white flex items-center gap-2.5">
            {tool?.name || 'Motorsport Live Timing'}
            {running && (playingBack
              ? <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-blue-900/30 text-blue-300 border border-blue-800">playback</span>
              : <HealthPill state={summary?.health?.state} />)}
            {running && summary?.frozen && (
              <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-cyan-900/30 text-cyan-300 border border-cyan-800">frozen</span>
            )}
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">
            {tool?.description || 'Follow Speedhive, Alpha Timing or Apex Timing live timing and publish broadcast-ready payloads to Singular data streams'}
          </p>
        </div>
        {running && (
          <button
            onClick={handleStop}
            disabled={stopping}
            className="flex-shrink-0 px-4 py-2 bg-red-900/40 border border-red-700 hover:bg-red-900/60 disabled:opacity-40 text-red-300 text-sm rounded transition-colors"
          >
            {stopping ? 'Stopping…' : playingBack ? 'Stop playback' : 'Stop live timing'}
          </button>
        )}
      </div>

      {error && <div className="mb-4 px-4 py-3 bg-red-900/30 border border-red-700 rounded text-red-400 text-sm">{error}</div>}

      <div className="mb-5">
        <label className="block text-[10px] font-medium text-gray-500 mb-1 uppercase tracking-wide">Show</label>
        <select
          value={showId}
          onChange={e => setShowId(e.target.value)}
          disabled={running}
          className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white disabled:opacity-60 focus:outline-none focus:border-blue-500"
        >
          <option value="">Pick a show</option>
          {shows.map(s => <option key={s.id} value={s.id}>{s.name}{s.status === 'live' ? ' (live)' : ''}</option>)}
        </select>
        {running && <p className="text-[10px] text-gray-600 mt-1">Stop live timing to bind it to a different show.</p>}
      </div>

      <div className="flex gap-1 mb-5 border-b border-[#2a2a2a]">
        {TABS.map(t => {
          const disabled = !ALWAYS_OPEN.has(t.key) && !running;
          return (
            <button
              key={t.key}
              onClick={() => !disabled && setTab(t.key)}
              disabled={disabled}
              title={disabled ? 'Available once live timing is running' : undefined}
              className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
                tab === t.key ? 'border-blue-500 text-white' : 'border-transparent text-gray-500 hover:text-gray-300'
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'setup' && (
        <SetupPanel show={show} running={running} onStarted={(data) => { setSummary({ running: true, ...data }); setTab('live'); }} />
      )}
      {tab === 'live' && running && <LivePanel summary={summary} showId={showId} onChanged={refresh} />}
      {tab === 'diagnostics' && running && <DiagnosticsPanel summary={summary} showId={showId} />}
      {tab === 'recordings' && (
        <RecordingsPanel show={show} summary={summary} onChanged={refresh} onPlaybackStarted={() => refresh().then(() => setTab('live'))} />
      )}

      {running && !playingBack && (
        <p className="mt-8 pt-4 border-t border-[#2a2a2a] text-[10px] text-gray-600 leading-relaxed">
          Live timing runs inside the Mission Control backend, so a backend deploy stops it until it is started again. Singular
          keeps the last payload it received, so graphics keep showing the last data throughout, but the timing stops moving.
          Avoid deploying mid-race.
        </p>
      )}
    </div>
  );
}
